/**
 * The entry set, and everything derived from it. Architecture Section 7.
 *
 * ## Why this holds ALL entries rather than a month at a time
 *
 * Data model Section 8: a decade of daily entries is a few thousand records and
 * a few megabytes. Full derivation at that volume is cheap, so the whole set is
 * loaded once and every read is synchronous afterwards.
 *
 * That is not just convenience. It is the same shape ADR-0006 needs: once
 * at-rest encryption lands in v0.4, the set is decrypted into memory on unlock
 * and there is nowhere for a partially-decrypted state to hide. Building the
 * per-month paging version now and replacing it then would be work done twice.
 *
 * It also means there is no cache of derived values, and therefore no
 * stale-derived-value bug to chase (FR-001).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  computeStatistics,
  deriveCycles,
  today as todayOf,
  type CycleStatistics,
  type DayEntry,
  type DerivedCycles,
  type ISODate,
} from '@domain/index'
import {
  checkStorageHealth,
  deleteEntry as deleteEntryRecord,
  getAllEntries,
  putEmptyEntry,
  putEntry,
  recordStorageEvidence,
  type EntryPatch,
  type StorageHealth,
} from '@shared/data/private'
import { useSettings } from './SettingsContext'

export type LoadState = 'loading' | 'ready' | 'error'

/** NFR-05: every failure state is designed, including the ones that persist. */
export type WriteProblem = 'quota' | 'failed' | 'premium' | undefined

export interface EntryContextValue {
  state: LoadState
  entries: ReadonlyMap<ISODate, DayEntry>
  today: ISODate
  derived: DerivedCycles
  statistics: CycleStatistics
  storage: StorageHealth | undefined
  writeProblem: WriteProblem
  reload: () => Promise<void>
  update: (date: ISODate, patch: EntryPatch) => Promise<void>
  markEmpty: (date: ISODate) => Promise<void>
  remove: (date: ISODate) => Promise<void>
  dismissWriteProblem: () => void
}

const EntryContext = createContext<EntryContextValue | undefined>(undefined)

export function EntryProvider({ children }: { children: ReactNode }) {
  // Captured once per mount. `domain/` never reads the clock itself, so the
  // current date is passed in — which is what makes derivation testable.
  const today = useMemo(() => todayOf(), [])

  const { isPremium } = useSettings()
  const [entries, setEntries] = useState<Map<ISODate, DayEntry>>(new Map())
  const [state, setState] = useState<LoadState>('loading')
  const [storage, setStorage] = useState<StorageHealth>()
  const [writeProblem, setWriteProblem] = useState<WriteProblem>()

  const reload = useCallback(async () => {
    try {
      const rows = await getAllEntries()
      setEntries(new Map(rows.map((e) => [e.date, e])))
      setState('ready')

      // R-04: leave a breadcrumb so a later run can tell "you never logged
      // anything" from "your data was evicted".
      recordStorageEvidence(rows.length)
      setStorage(await checkStorageHealth(rows.length))
    } catch {
      setState('error')
      setStorage(await checkStorageHealth(0).catch(() => undefined))
    }
  }, [])

  useEffect(() => {
    // The lint rule traces `reload` to a setState and assumes a synchronous
    // cascade. It is not one: every setState runs after an awaited IndexedDB
    // read. Loading persisted state on mount is what an effect is for.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload()
  }, [reload])

  const applyWrite = useCallback(
    async (write: () => Promise<DayEntry | void>, date: ISODate) => {
      if (!isPremium && !entries.has(date)) {
        if (entries.size >= 90) {
          setWriteProblem('premium')
          return
        }
      }

      try {
        const saved = await write()
        setEntries((previous) => {
          const next = new Map(previous)
          if (saved) next.set(date, saved)
          else next.delete(date)
          return next
        })
        setWriteProblem(undefined)
      } catch (error) {
        const { isQuotaError } = await import('@shared/data/private/storageHealth')
        setWriteProblem(isQuotaError(error) ? 'quota' : 'failed')
        // Re-read so the UI shows what is actually stored rather than an
        // optimistic value that never landed. Silently keeping a value the
        // database rejected is the worst option available.
        await reload()
      }
    },
    [reload, isPremium, entries],
  )

  const update = useCallback(
    (date: ISODate, patch: EntryPatch) => applyWrite(() => putEntry(date, patch), date),
    [applyWrite],
  )

  const markEmpty = useCallback(
    (date: ISODate) => applyWrite(() => putEmptyEntry(date), date),
    [applyWrite],
  )

  const remove = useCallback(
    (date: ISODate) => applyWrite(() => deleteEntryRecord(date), date),
    [applyWrite],
  )

  const derived = useMemo(
    () => deriveCycles([...entries.values()], { today }),
    [entries, today],
  )

  // Prediction is NOT here: it also depends on the suspended setting, so it is
  // composed in `usePrediction` where both contexts are in scope.
  const statistics = useMemo(
    () => computeStatistics(derived, [...entries.values()]),
    [derived, entries],
  )

  const value = useMemo<EntryContextValue>(
    () => ({
      state,
      entries,
      today,
      derived,
      statistics,
      storage,
      writeProblem,
      reload,
      update,
      markEmpty,
      remove,
      dismissWriteProblem: () => setWriteProblem(undefined),
    }),
    [
      state,
      entries,
      today,
      derived,
      statistics,
      storage,
      writeProblem,
      reload,
      update,
      markEmpty,
      remove,
    ],
  )

  return <EntryContext.Provider value={value}>{children}</EntryContext.Provider>
}

export function useEntries(): EntryContextValue {
  const value = useContext(EntryContext)
  if (value === undefined) throw new Error('useEntries used outside EntryProvider')
  return value
}
